import React, { useEffect, useRef, useState } from "react";
import SearchPanel from "../../User/component/SearchPanel";
import { AdminPartnerSearchItem, ListPartnerSearchAdmin } from "../../../../services/UserService";

/**
 * Ô "Filter by keyword" của màn Station Management, có GỢI Ý trong lúc gõ.
 *
 * Vì sao cần: danh sách station lọc theo `name` — tức TÊN NỘI BỘ (tên khách sạn). Gõ tên
 * công khai ("Luggage storage Bukit Bintang") thì bảng ra rỗng dù station đó có thật.
 * Gợi ý tra bằng `/admin-partner-search` (tìm theo CẢ hai tên) rồi điền lại tên nội bộ vào
 * ô lọc, nên gõ kiểu nào cũng tới được đúng station.
 *
 * Bọc ngoài SearchPanel, KHÔNG sửa SearchPanel — ô đó còn dùng ở màn đơn hàng và người dùng.
 */
export default function PartnerKeywordSearch(props: {
	filterKeyword: string | null | undefined;
	doFilterKeyword: ((val: string | null) => void) | undefined;
}) {
	const [typed, setTyped] = useState(props.filterKeyword || "");
	const [suggestions, setSuggestions] = useState<AdminPartnerSearchItem[]>([]);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const wrapRef = useRef<HTMLDivElement>(null);
	const requestSeq = useRef(0);
	// Tên vừa chọn từ gợi ý: nó được điền ngược vào ô, và nếu không nhớ lại thì lượt tra
	// tiếp theo sẽ bật đúng cái danh sách vừa đóng.
	const justPicked = useRef<string | null>(null);

	useEffect(() => {
		setTyped(props.filterKeyword || "");
	}, [props.filterKeyword]);

	// Đợi người dùng ngừng gõ rồi mới hỏi server, tránh mỗi phím một lượt gọi.
	useEffect(() => {
		const keyword = typed.trim();
		if (justPicked.current !== null && keyword === justPicked.current) {
			setLoading(false);
			return;
		}
		justPicked.current = null;
		if (keyword.length < 2) {
			setSuggestions([]);
			setLoading(false);
			return;
		}

		const seq = ++requestSeq.current;
		setLoading(true);
		const timer = window.setTimeout(async () => {
			try {
				// includeInactive: đây là màn quản trị, phải thấy cả station đã tắt.
				const rows = await ListPartnerSearchAdmin(keyword, true);
				if (seq !== requestSeq.current) return; // kết quả cũ về muộn thì bỏ
				setSuggestions(rows.slice(0, 8));
				setOpen(true);
			} catch (err) {
				console.error(err);
				if (seq === requestSeq.current) setSuggestions([]);
			} finally {
				if (seq === requestSeq.current) setLoading(false);
			}
		}, 300);

		return () => window.clearTimeout(timer);
	}, [typed]);

	useEffect(() => {
		const onClickOutside = (event: MouseEvent) => {
			if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", onClickOutside);
		return () => document.removeEventListener("mousedown", onClickOutside);
	}, []);

	const pick = (item: AdminPartnerSearchItem) => {
		justPicked.current = (item.name || "").trim();
		// Một lượt tra có thể đang bay: hủy timer không chặn được promise đã gửi đi, và khi
		// nó về sẽ mở lại đúng cái danh sách vừa đóng. Tăng số thứ tự để câu trả lời đó rơi.
		requestSeq.current += 1;
		setLoading(false);
		setOpen(false);
		setTyped(item.name || "");
		// Bảng lọc theo tên nội bộ, nên luôn điền tên nội bộ dù người dùng gõ tên công khai.
		props.doFilterKeyword?.(item.name || "");
	};

	return (
		<div
			ref={wrapRef}
			style={{ position: "relative" }}
			onKeyDown={(e) => {
				if (e.key === "Escape") setOpen(false);
			}}>
			<div
				onInput={(e) => setTyped((e.target as HTMLInputElement).value)}
				onFocus={() => suggestions.length > 0 && setOpen(true)}>
				<SearchPanel
					placeholder="Keyword"
					filterKeyword={props.filterKeyword}
					doFilterKeyword={(val) => {
						setOpen(false);
						props.doFilterKeyword?.(val);
					}}
				/>
			</div>

			{/* Đang gõ dở = chữ trong ô KHÁC bộ lọc đang áp. Chọn xong hai chuỗi bằng nhau nên
			    danh sách tự tắt — điều kiện suy ra từ trạng thái, không phụ thuộc thứ tự sự kiện
			    (bản trước tắt bằng cờ, và câu trả lời mạng về muộn lại bật nó lên). */}
			{open &&
				typed.trim() !== (props.filterKeyword || "").trim() &&
				(loading || suggestions.length > 0) && (
				<div
					style={{
						position: "absolute",
						zIndex: 20,
						top: "100%",
						left: 0,
						right: 0,
						marginTop: 4,
						background: "#fff",
						border: "1px solid #d9d9d9",
						borderRadius: 8,
						boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
						maxHeight: 320,
						overflowY: "auto",
					}}>
					{loading && suggestions.length === 0 ? (
						<div style={{ padding: "10px 12px", fontSize: 13, color: "#666" }}>Đang tìm…</div>
					) : (
						suggestions.map((item) => {
							const publicName = item.displayName && item.displayName !== item.name ? item.displayName : "";
							const state = item.isBanned ? "đã ngừng hợp tác" : item.active === false ? "đang tắt" : "";
							return (
								<div
									key={item.id}
									onMouseDown={(e) => {
										e.preventDefault();
										pick(item);
									}}
									style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, lineHeight: 1.4 }}
									onMouseEnter={(e) => (e.currentTarget.style.background = "#f2f7ff")}
									onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
									<div style={{ fontWeight: 600 }}>
										#{item.id} · {item.name}
										{state ? <span style={{ color: "#c0392b", fontWeight: 400 }}> ({state})</span> : null}
									</div>
									{publicName ? <div style={{ color: "#666" }}>{publicName}</div> : null}
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
}
